import { Box, Container, Link } from "@mui/material";
import { ThemeProvider, lighten } from "@mui/material/styles";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { CTextField } from "@/components/TextField/CTextField";
import { CTextFieldPassword } from "@/components/TextField/CTextFieldPassword";
import { SubmitButton, ButtonVariantOption } from "@/components/Button";
import type { ILoginPresenterProps } from "./types/index";
import type { LoginFormData } from "./types/schemas";
import React from "react";
import LoginIcon from "@mui/icons-material/Login";
import { format } from "date-fns";
import ja from "date-fns/locale/ja";
import { NewYearHolidayNotice } from "./components/NewYearHolidayNotice";
import { ServiceUpdateNotice } from "./components/ServiceUpdateNotice";
import { LinkButton } from "@/components/Button/LinkButton";
import { ButtonColorOption } from "@/components/Button/Button";
import NoteAdd from "@mui/icons-material/NoteAdd";
import logo_color from "../../assets/logo_color.svg";
import { theme } from "@/types/theme";

const LoginPresenter: React.FC<ILoginPresenterProps> = (props) => {
  const { loginForm, handleSubmit } = props;

  // ログインボタンをスクリプトからクリックするために使用するref
  const ref = React.useRef<HTMLButtonElement>(null);

  // テキストボックスの有効/無効を制御するstate
  const [loading, setLoading] = React.useState(false);

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      ref.current?.click();
    }
  };

  // ログインUIのリセット
  const resetLoginUI = (cancelLoading: () => void) => {
    return () => {
      cancelLoading();
      setLoading(false);
    };
  };

  const handleClick = (
    _: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    cancelLoading: () => void
  ): void => {
    setLoading(true);

    void loginForm.handleSubmit(
      async (data: LoginFormData) => {
        await handleSubmit(data, resetLoginUI(cancelLoading));
      },
      () => {
        // バリデーションエラー時の処理
        resetLoginUI(cancelLoading)();
      }
    )();
  };

  const today = new Date();
  const notifyStartDate = new Date(2026, 11, 21); // 2026年12月21日（月） - 年末年始休業のお知らせは12月第3週目から表示
  const notifyEndDate = new Date(2027, 0, 4); // 2027年1月4日（月） - 仕事始めの日
  const holidayStartDateText = format(
    new Date(2026, 11, 31),
    "yyyy/MM/dd（eee）",
    { locale: ja }
  );
  const holidayEndDateText = format(new Date(2027, 0, 3), "yyyy/MM/dd（eee）", {
    locale: ja,
  }); // 2027年1月3日（日） - 年末年始休業の最終日
  const businessStartDateText = format(notifyEndDate, "yyyy/MM/dd（eee）", {
    locale: ja,
  });

  // 利用規約制定・料金改定のお知らせ表示期間
  const serviceUpdateNotifyEndDate = new Date(2026, 2, 31); // 2026年3月31日

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          py: 10,
          backgroundColor: lighten(theme.palette.bluegray.main, 0.8),
          alignItems: "center",
        }}
      >
        {/* ロゴ */}
        <Box
          sx={{
            flexGrow: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 4,
          }}
        >
          <img
            src={logo_color}
            alt="logo"
            height="48px"
            width="90%"
            style={{
              marginBottom: 8,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          />
        </Box>
        {/* ログインフォームコンテナ */}
        <Container
          maxWidth="xs"
          sx={{
            backgroundColor: theme.palette.common.white,
            borderRadius: 6,
            border: {
              xs: "none",
              sm: `1px solid ${theme.palette.border.main}`,
            },
            py: { xs: 4, sm: 6 },
            px: { xs: 2, sm: 4 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* SPは入力欄を親幅いっぱいに広げる（親が flex 中央寄せで内容幅に縮むため） */}
          <Box component="form" sx={{ width: { xs: "100%", sm: "auto" } }}>
            {/* メールアドレス */}
            <Box sx={{ my: 2, mb: 1 }}>
              <CTextField
                control={loginForm.control}
                name="email"
                id="email"
                label="メールアドレス"
                required={false}
                autoFocus={true}
                size={SizingWrapperStyle.LARGE}
                disabled={loading}
                center={true}
              />
            </Box>
            {/* パスワード */}
            <Box sx={{ my: 2, mb: 1 }}>
              <CTextFieldPassword
                control={loginForm.control}
                name="password"
                autoComplete="current-password"
                required={false}
                onKeyUp={handleKeyUp}
                size={SizingWrapperStyle.LARGE}
                disabled={loading}
                center={true}
              />
            </Box>

            {/* ログインボタン */}
            <Box sx={{ my: 3 }}>
              <SubmitButton
                label={"ログイン"}
                onClick={handleClick}
                variant={ButtonVariantOption.Contained}
                size={SizingWrapperStyle.LARGE}
                loadingIndicator={"ログイン中..."}
                timeout={30000}
                refToClickButton={ref}
                onTimeout={() => {
                  setLoading(false);
                }}
                startIcon={<LoginIcon />}
                sx={{
                  fontWeight: 600,
                  p: 1.5,
                  borderRadius: 2,
                }}
              />
            </Box>
          </Box>
          {/* プライバシーポリシー・利用規約 */}
          <Box
            sx={{
              mt: 2,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Link
              href="https://www.trustart.co.jp/privacy/"
              sx={{ display: "inline-block", fontSize: 13 }}
              target="_blank"
              rel="noopener"
            >
              プライバシーポリシー
            </Link>
            <Link
              href="https://www.trustart.co.jp/redata_plus_terms/"
              sx={{ display: "inline-block", mt: 1, fontSize: 13 }}
              target="_blank"
              rel="noopener"
            >
              サービス利用規約
            </Link>
          </Box>
        </Container>
        {/* アカウント開設申込フォーム */}
        <Box
          sx={{
            mt: 4,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <LinkButton
            variant={ButtonVariantOption.Outlined}
            href="https://docs.google.com/forms/d/e/1FAIpQLSdLsQTUVUtEeZfQjDEkFcgilR5PQXga_qtMLDUf9IJsZtZDEQ/viewform?usp=pp_url"
            wrapperSize={SizingWrapperStyle.LARGE}
            size={SizingWrapperStyle.LARGE}
            color={ButtonColorOption.Success}
            target="_blank"
            rel="noopener"
            sx={{
              p: 2,
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              justifyContent: "center",
              fontSize: { xs: "12px", sm: "16px" },
              width: { xs: "80%", sm: "100%" },
              mx: "auto",
            }}
          >
            <NoteAdd fontSize="medium" />
            <span>アカウント開設の申込はこちら</span>
          </LinkButton>
        </Box>
        {/* 年末年始休業のお知らせ */}
        {today >= notifyStartDate && today <= notifyEndDate && (
          <Box
            sx={{
              mt: 4,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <NewYearHolidayNotice
              holidayStartDateText={holidayStartDateText}
              holidayEndDateText={holidayEndDateText}
              businessStartDateText={businessStartDateText}
            />
          </Box>
        )}
        {/* 利用規約制定・料金改定のお知らせ */}
        {today <= serviceUpdateNotifyEndDate && (
          <Box
            sx={{
              mt: 4,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <ServiceUpdateNotice />
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

export { LoginPresenter };
